import { format, isSameDay } from "date-fns";
import { toZonedTime } from "date-fns-tz";
import { getSchedules, Schedule, AvailabilityType } from "app/redux/schedulesSlice";
import { getUserTimezone } from "app/utils/timezone";
import { generateTimeSlots, parseTime } from "./timeSlots";

export type ScheduleMap = { [employeeId: number]: { [date: string]: Schedule } };

// Fetches schedules for a set of employees on a single date and indexes them
// by employeeId -> date, skipping employees who aren't scheduled at all that day.
// A provider with no working hours set up comes back with `availability: null`;
// skip them too, or one such provider empties the calendar for everyone.
export const getEmployeeSchedulesForDates = async (
  dispatch: any,
  employeeIds: number[],
  date: Date,
  nameKey: string
): Promise<ScheduleMap> => {
  const schedules = await dispatch(
    getSchedules({ employeeIds, date, nameKey }) as any
  ).unwrap();

  const scheduleMap: ScheduleMap = {};

  schedules.forEach((schedule: Schedule) => {
    const { employeeId, date: scheduleDate, availability } = schedule;
    if (!availability || availability.status === AvailabilityType.UNSCHEDULED) {
      return;
    }
    if (!scheduleMap[employeeId]) {
      scheduleMap[employeeId] = {};
    }
    scheduleMap[employeeId][scheduleDate] = schedule;
  });

  return scheduleMap;
};

// The part of [startIso, endIso) that falls on `dateString` ("yyyy-MM-dd"), as
// minutes-since-midnight in the SHOP's timezone - so it lines up with the
// "09:00"-"17:00" business-hours strings. Null when it's on another day: comparing
// time of day alone let a one-off Saturday "Off" block every day's slots.
export const minutesOnDate = (
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  dateString: string
): { start: number; end: number } | null => {
  if (!startIso || !endIso) return null;
  const timezone = getUserTimezone();
  const start = toZonedTime(startIso, timezone);
  const end = toZonedTime(endIso, timezone);
  const startDay = format(start, "yyyy-MM-dd");
  const endDay = format(end, "yyyy-MM-dd");
  if (startDay > dateString || endDay < dateString) return null;

  const startMinutes = startDay < dateString ? 0 : start.getHours() * 60 + start.getMinutes();
  const endMinutes = endDay > dateString ? 24 * 60 : end.getHours() * 60 + end.getMinutes();
  return startMinutes < endMinutes ? { start: startMinutes, end: endMinutes } : null;
};

// Whether [startMinutes, endMinutes) on `dateString` collides with any of `items`.
export const overlapsAnyOnDate = (
  items: { startTime: string; endTime: string }[] | undefined,
  dateString: string,
  startMinutes: number,
  endMinutes: number
): boolean =>
  (items || []).some((item) => {
    const range = minutesOnDate(item.startTime, item.endTime, dateString);
    return !!range && startMinutes < range.end && endMinutes > range.start;
  });

// Whether one employee's schedule is free for [startMinutes, endMinutes) -
// inside their availability window, and clear of appointments/blocked times.
const isEmployeeFreeForWindow = (
  schedule: Schedule | undefined,
  startMinutes: number,
  endMinutes: number
): boolean => {
  if (!schedule || !schedule.availability) return false;

  const { availability, appointments, blockedTimes } = schedule as any;
  const appointmentGroups = (schedule as any).appointmentGroups;
  const groupAppointments = appointmentGroups
    ? appointmentGroups.flatMap((group: any) => group.appointments || [])
    : [];
  const allAppointments = [...(appointments || []), ...groupAppointments];

  if (
    startMinutes < parseTime(availability.timeStart) ||
    endMinutes > parseTime(availability.timeEnd)
  ) {
    return false;
  }

  return (
    !overlapsAnyOnDate(allAppointments, schedule.date, startMinutes, endMinutes) &&
    !overlapsAnyOnDate(blockedTimes, schedule.date, startMinutes, endMinutes)
  );
};

// Start times (HH:mm) at which this one employee is free for the full service duration.
const getFreeStartTimesForEmployee = (
  schedule: Schedule | undefined,
  duration: number,
  interval: number,
  isSameDayFlag: boolean,
  currentDate: Date
): string[] => {
  if (!schedule || !schedule.availability) return [];

  const possibleStarts = generateTimeSlots(
    schedule.availability.timeStart,
    schedule.availability.timeEnd,
    interval
  ).map((slot) => slot.split(" - ")[0]);

  return possibleStarts.filter((startStr) => {
    const startMinutes = parseTime(startStr);

    if (
      isSameDayFlag &&
      startMinutes < currentDate.getHours() * 60 + currentDate.getMinutes()
    ) {
      return false;
    }

    return isEmployeeFreeForWindow(schedule, startMinutes, startMinutes + duration);
  });
};

// Union of start times where ANY of the given employees could take the service -
// used to drive the calendar when no provider has been picked yet.
export const findAnyEmployeeAvailableTimeSlots = ({
  schedules,
  employeeIds,
  duration,
  date,
  interval,
  currentDate,
}: {
  schedules: ScheduleMap;
  employeeIds: number[];
  duration: number;
  date: Date;
  interval: number;
  currentDate: Date;
}): string[] => {
  const dateString = format(date, "yyyy-MM-dd");
  const isSameDayFlag = isSameDay(currentDate, date);

  const allFreeStarts = new Set<string>();

  employeeIds.forEach((employeeId) => {
    const schedule = schedules[employeeId]?.[dateString];
    getFreeStartTimesForEmployee(
      schedule,
      duration,
      interval,
      isSameDayFlag,
      currentDate
    ).forEach((start) => allFreeStarts.add(start));
  });

  return Array.from(allFreeStarts).sort();
};

// Which of the given employees are free for the full service duration starting at `timeStr`
// on `date` - used once a slot is picked, to narrow the provider list to who can take it.
export const getEmployeesAvailableAtSlot = ({
  schedules,
  employeeIds,
  duration,
  date,
  timeStr,
}: {
  schedules: ScheduleMap;
  employeeIds: number[];
  duration: number;
  date: Date;
  timeStr: string;
}): number[] => {
  const dateString = format(date, "yyyy-MM-dd");
  const startMinutes = parseTime(timeStr);
  const endMinutes = startMinutes + duration;

  return employeeIds.filter((employeeId) =>
    isEmployeeFreeForWindow(
      schedules[employeeId]?.[dateString],
      startMinutes,
      endMinutes
    )
  );
};
